import React, { useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';

import {
  useDownloadJetstarBspImportErrorsCsv,
  useJetstarBspImportResults,
  useJetstarBspImportStatus,
  useUploadJetstarBspImport,
} from '~/hooks/apis/jetstar-bsp/useJetstarBspImports';
import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { JetstarBspImportFileType, JetstarBspImportResultItem } from '~/services/JetstarBspService';

const IMPORT_FILE_TYPE_LABELS: Record<JetstarBspImportFileType, string> = {
  jetstar_bsp: 'Jetstar BSP',
  travelfusion: 'TravelFusion',
};

function getErrorMessages(error: unknown): Array<string> {
  if (!error || typeof error !== 'object') {
    return ['Unexpected error'];
  }

  const apiError = error as {
    message?: string;
    errors?: unknown;
  };

  if (Array.isArray(apiError.errors) && apiError.errors.length > 0) {
    const parsedErrors = apiError.errors
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item && typeof item === 'object' && 'message' in item && typeof item.message === 'string') {
          return item.message;
        }
        return '';
      })
      .filter(Boolean);

    if (parsedErrors.length > 0) {
      return parsedErrors;
    }
  }

  if (apiError.message && apiError.message !== '400') {
    return [apiError.message];
  }

  return ['Upload failed due to CSV validation. Please review the file and try again.'];
}

function getErrorMessage(error: unknown): string {
  return getErrorMessages(error)[0] || 'Unexpected error';
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString();
}

function getImportStatusColor(status: string): 'default' | 'warning' | 'info' | 'success' | 'error' {
  if (status === 'completed') {
    return 'success';
  }
  if (status === 'completed_with_errors') {
    return 'warning';
  }
  if (status === 'processing') {
    return 'info';
  }
  if (status === 'failed') {
    return 'error';
  }
  return 'default';
}

function getPnrStatusColor(status: string): 'default' | 'warning' | 'info' | 'success' | 'error' {
  if (status === 'success') {
    return 'success';
  }
  if (status === 'failed') {
    return 'error';
  }
  if (status === 'processing') {
    return 'info';
  }
  if (status === 'pending') {
    return 'warning';
  }
  return 'default';
}

export default function JetstarBspImportMonitoringPage() {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const query = useQuery();
  const { replace } = useAppNavigation();
  const location = useLocation();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<JetstarBspImportFileType>('jetstar_bsp');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const selectedImportId = query.get('importId');
  const uploadMutation = useUploadJetstarBspImport();
  const statusQuery = useJetstarBspImportStatus(selectedImportId);
  const resultsQuery = useJetstarBspImportResults(selectedImportId, page + 1, pageSize);
  const downloadErrorsMutation = useDownloadJetstarBspImportErrorsCsv();

  const rows = resultsQuery.data?.results ?? [];
  const totalRows = resultsQuery.data?.pagination.total ?? 0;
  const statusCounters = statusQuery.data?.counters;
  const isUploading = uploadMutation.isPending;

  const columns = useMemo<Array<GridColDef<JetstarBspImportResultItem>>>(
    () => [
      {
        field: 'pnr',
        headerName: 'PNR',
        minWidth: 140,
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 140,
        flex: 1,
        renderCell: ({ value }) => <Chip size="small" label={value} color={getPnrStatusColor(`${value}`)} />,
      },
      {
        field: 'reasonCode',
        headerName: 'Reason Code',
        minWidth: 220,
        flex: 1,
        valueGetter: (_, row) => row.reasonCode || '-',
      },
      {
        field: 'reasonMessage',
        headerName: 'Reason Message',
        minWidth: 280,
        flex: 2,
        valueGetter: (_, row) => row.reasonMessage || '-',
      },
      {
        field: 'orderNumber',
        headerName: 'Order Number',
        minWidth: 180,
        flex: 1,
        valueGetter: (_, row) => row.orderNumber || '-',
      },
      {
        field: 'processedAt',
        headerName: 'Processed At',
        minWidth: 210,
        flex: 1,
        valueGetter: (_, row) => formatDateTime(row.processedAt),
      },
    ],
    [],
  );

  const setImportIdInQuery = (importId: string | null) => {
    const queryParams = new URLSearchParams(location.search);

    if (importId) {
      queryParams.set('importId', importId);
    } else {
      queryParams.delete('importId');
    }

    const nextSearch = queryParams.toString();
    replace(nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      enqueueSnackbar('Select a CSV file before uploading', { variant: 'warning' });
      return;
    }

    uploadMutation.mutate(
      {
        file: selectedFile,
        fileType: selectedFileType,
      },
      {
        onSuccess: (result) => {
          enqueueSnackbar(`${IMPORT_FILE_TYPE_LABELS[selectedFileType]} import ${result.importId} created`, {
            variant: 'success',
          });
          setPage(0);
          setImportIdInQuery(result.importId);
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
        onError: (error) => {
          enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
        },
      },
    );
  };

  const handleFileTypeChange = (event: SelectChangeEvent<JetstarBspImportFileType>) => {
    setSelectedFileType(event.target.value as JetstarBspImportFileType);
  };

  const handleDownloadErrors = () => {
    if (!selectedImportId) {
      enqueueSnackbar('Upload a CSV first or select an existing import', { variant: 'warning' });
      return;
    }

    downloadErrorsMutation.mutate(selectedImportId, {
      onSuccess: () => {
        enqueueSnackbar('Errors CSV download started', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      },
    });
  };

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Travel Ops Post-ticket Import Monitoring</Typography>
          <Alert severity="info">
            Upload a Jetstar BSP or TravelFusion CSV file to start import processing. This page polls the current import
            status and shows per-PNR reconciliation outcomes.
          </Alert>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="travel-ops-import-file-type-label">File type</InputLabel>
              <Select
                labelId="travel-ops-import-file-type-label"
                label="File type"
                value={selectedFileType}
                onChange={handleFileTypeChange}
                disabled={isUploading}
              >
                <MenuItem value="jetstar_bsp">Jetstar BSP</MenuItem>
                <MenuItem value="travelfusion">TravelFusion</MenuItem>
              </Select>
            </FormControl>
            <TextField
              inputRef={fileInputRef}
              inputProps={{ accept: '.csv' }}
              type="file"
              onChange={(event) => {
                const input = event.target as HTMLInputElement;
                const file = input.files?.[0] || null;
                setSelectedFile(file);
              }}
              sx={{ minWidth: 360, flexGrow: 1 }}
            />
            <Button variant="contained" startIcon={<UploadFileIcon />} onClick={handleUpload} disabled={isUploading}>
              {isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadErrors}
              disabled={!selectedImportId || downloadErrorsMutation.isPending}
            >
              Download Errors CSV
            </Button>
          </Stack>
          {uploadMutation.isError && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Upload failed. Please fix the CSV and try again.
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                {getErrorMessages(uploadMutation.error).map((errorMessage, index) => (
                  <li key={`${errorMessage}-${index}`}>
                    <Typography variant="body2">{errorMessage}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="h6">Import Status</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  statusQuery.refetch();
                  resultsQuery.refetch();
                }}
                disabled={!selectedImportId}
              >
                Refresh
              </Button>
              <Button
                variant="text"
                color="inherit"
                disabled={!selectedImportId}
                onClick={() => {
                  setImportIdInQuery(null);
                  setPage(0);
                }}
              >
                Clear Import
              </Button>
            </Stack>
          </Stack>

          {!selectedImportId && <Alert severity="warning">No import selected. Upload a CSV to begin monitoring.</Alert>}

          {selectedImportId && (
            <Stack spacing={2}>
              <Typography variant="body2">Import ID: {selectedImportId}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Status: ${statusQuery.data?.status || '-'}`}
                  color={getImportStatusColor(`${statusQuery.data?.status || ''}`)}
                />
                <Chip label={`Total Rows: ${statusCounters?.totalRows ?? '-'}`} />
                <Chip label={`Total PNRs: ${statusCounters?.totalPnrs ?? '-'}`} />
                <Chip label={`Success PNRs: ${statusCounters?.successPnrs ?? '-'}`} color="success" />
                <Chip label={`Failed PNRs: ${statusCounters?.failedPnrs ?? '-'}`} color="error" />
              </Stack>
              {statusQuery.isError && <Alert severity="error">{getErrorMessage(statusQuery.error)}</Alert>}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Per-PNR Results</Typography>
          <Box sx={{ minHeight: 420 }}>
            <DataGridPro
              rows={rows}
              columns={columns}
              getRowId={(row) => `${row.pnr}-${row.processedAt || row.status}`}
              rowCount={totalRows}
              loading={resultsQuery.isLoading || resultsQuery.isFetching}
              pagination
              paginationMode="server"
              pageSizeOptions={[25, 50, 100]}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => {
                setPage(model.page);
                setPageSize(model.pageSize);
              }}
              disableColumnMenu
              disableRowSelectionOnClick
            />
          </Box>
          {resultsQuery.isError && <Alert severity="error">{getErrorMessage(resultsQuery.error)}</Alert>}
        </Stack>
      </Paper>
    </Stack>
  );
}
